import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, Mail, Phone, Building2 } from 'lucide-react';
import { useContacts, useCreateContact } from '@/hooks/useContacts';
import { useOrganizations } from '@/hooks/useOrganizations';
import {
  Button,
  Card,
  EmptyState,
  SearchInput,
  Modal,
  Input,
  Select,
  PageHeader,
  Skeleton,
} from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import type { ContactStatus, ContactSource } from '@/types';

const statusVariant: Record<ContactStatus, 'default' | 'info' | 'success' | 'error'> = {
  LEAD: 'default',
  QUALIFIED: 'info',
  CUSTOMER: 'success',
  CHURNED: 'error',
};

const sourceLabels: Record<ContactSource, string> = {
  MANUAL: 'Manual',
  FORM_SUBMISSION: 'Form',
  IMPORT: 'Import',
  HUBSPOT_IMPORTED: 'HubSpot',
  REFERRAL: 'Referral',
  WEBSITE: 'Website',
  MEETING: 'Meeting',
};

export default function Contacts() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useContacts({
    search: search || undefined,
    status: statusFilter || undefined,
  });

  const contacts = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="page-enter">
      <PageHeader
        title="Contacts"
        subtitle={`${total} contacts`}
        actions={
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4" />
            Add Contact
          </Button>
        }
      />

      <div className="flex items-center gap-3 mb-6">
        <SearchInput
          searches="contacts"
          value={search}
          onChange={setSearch}
          className="w-80"
        />
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: 'All Statuses' },
            { value: 'LEAD', label: 'Lead' },
            { value: 'QUALIFIED', label: 'Qualified' },
            { value: 'CUSTOMER', label: 'Customer' },
            { value: 'CHURNED', label: 'Churned' },
          ]}
          className="w-40"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-4">
              <Skeleton className="h-5 w-48" />
            </div>
          ))}
        </div>
      ) : contacts.length === 0 ? (
        <Card padding="md">
          <EmptyState
            icon={<Users />}
            title="No contacts yet"
            description="Add your first contact to start building your CRM."
            action={
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4" />
                Add Contact
              </Button>
            }
          />
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-grey-200">
                  <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Name</th>
                  <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Email</th>
                  <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Phone</th>
                  <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Organization</th>
                  <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Status</th>
                  <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-grey-100">
                {contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-grey-50 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/contacts/${contact.id}`}
                        className="font-medium text-secondary hover:text-primary transition-colors"
                      >
                        {contact.firstName} {contact.lastName}
                      </Link>
                      {contact.jobTitle && (
                        <p className="text-xs text-grey-500">{contact.jobTitle}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {contact.email ? (
                        <span className="flex items-center gap-1.5 text-sm text-grey-600">
                          <Mail className="w-3.5 h-3.5 text-grey-400" />
                          {contact.email}
                        </span>
                      ) : (
                        <span className="text-sm text-grey-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {contact.phone ? (
                        <span className="flex items-center gap-1.5 text-sm text-grey-600">
                          <Phone className="w-3.5 h-3.5 text-grey-400" />
                          {contact.phone}
                        </span>
                      ) : (
                        <span className="text-sm text-grey-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {contact.organization ? (
                        <Link
                          to={`/admin/organizations/${contact.organization.id}`}
                          className="flex items-center gap-1.5 text-sm text-grey-600 hover:text-primary"
                        >
                          <Building2 className="w-3.5 h-3.5 text-grey-400" />
                          {contact.organization.name}
                        </Link>
                      ) : (
                        <span className="text-sm text-grey-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={statusVariant[contact.status]}>
                        {contact.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-grey-500">
                        {sourceLabels[contact.source]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <CreateContactDialog open={showCreate} onOpenChange={setShowCreate} />
    </div>
  );
}

function CreateContactDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    organizationId: '',
  });
  const createContact = useCreateContact();
  const { data: orgs } = useOrganizations();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createContact.mutateAsync({
      ...form,
      email: form.email || undefined,
      phone: form.phone || undefined,
      jobTitle: form.jobTitle || undefined,
      organizationId: form.organizationId || undefined,
    });
    onOpenChange(false);
    setForm({ firstName: '', lastName: '', email: '', phone: '', jobTitle: '', organizationId: '' });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add Contact"
      description="Create a new contact in your CRM."
      footer={
        <>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="create-contact-form" loading={createContact.isPending}>
            Create Contact
          </Button>
        </>
      }
    >
      <form id="create-contact-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            required
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
          <Input
            label="Last Name"
            required
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
        </div>
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <Input
          label="Phone"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <Input
          label="Job Title"
          value={form.jobTitle}
          onChange={(e) => setForm({ ...form, jobTitle: e.target.value })}
        />
        <Select
          label="Organization"
          value={form.organizationId}
          onChange={(e) => setForm({ ...form, organizationId: e.target.value })}
          options={[
            { value: '', label: 'None' },
            ...(orgs?.data ?? []).map((o) => ({ value: o.id, label: o.name })),
          ]}
        />
      </form>
    </Modal>
  );
}
